import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { api } from "../../lib/api";
import { toast } from "sonner";
import Brand from "../../components/Brand";
import DashboardGrid from "./DashboardGrid";
import { Heart, KeyRound, Calendar, Crown, Send, ChevronRight, Lock } from "lucide-react";

/* ============================================================
   COMMAND CENTER — the back-office home page for members.
   ClickFunnels-style grid is THE dashboard.  The greeting plaque
   (clock + verse + Mike's note) sits on top.  Password-change card
   stays inline so Mike never has to hunt for it.  Prayer box anchors
   the side rail beneath the grid.
   ============================================================ */

function LiveClock({ compact = false }) {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);
  const date = now.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" });
  const time = now.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
  if (compact) {
    return <span className="text-xs text-foreground/70 font-mono">{time} · {date}</span>;
  }
  return (
    <div className="flex items-baseline gap-3" data-testid="cmd-clock">
      <div className="font-serif-display text-5xl md:text-6xl text-red-wine leading-none" style={{ fontWeight: 700 }}>{time}</div>
      <div className="text-foreground/70 text-sm tracking-wider-cap hidden sm:block">{date}</div>
    </div>
  );
}

function greetingForHour() {
  const h = new Date().getHours();
  if (h < 5)  return "Peace upon you tonight";
  if (h < 12) return "Good morning";
  if (h < 17) return "Good afternoon";
  if (h < 21) return "Good evening";
  return "Peace upon you tonight";
}

/* ---- Trial banner — locked OR <=7 days left ---- */
function TrialBanner({ user }) {
  if (!user) return null;
  const tier = (user.tier || "free").toLowerCase();
  if (tier !== "free") return null;
  const locked = !!user.trial_locked;
  const days = Math.max(0, Number(user.trial_days_left || 0));
  if (!locked && days > 7) return null;
  return locked ? (
    <Link to="/pricing"
          data-testid="trial-banner-locked"
          className="block rounded-2xl p-5 shadow-md hover:shadow-lg transition no-underline"
          style={{ background: "linear-gradient(135deg, #0F172A 0%, #8B2A2A 100%)", border: "3px solid #F39A1F" }}>
      <div className="flex items-center gap-3 flex-wrap">
        <div className="w-11 h-11 rounded-full flex items-center justify-center text-white" style={{ background: "#F39A1F" }}>
          <Lock className="w-5 h-5"/>
        </div>
        <div className="flex-1 min-w-[200px]">
          <div className="text-[10px] tracking-[0.32em] uppercase font-bold" style={{ color: "#E0F2FE" }}>
            ★ YOUR 7-DAY FREE TRIAL HAS ENDED
          </div>
          <div className="font-serif-display text-xl md:text-2xl uppercase text-white leading-tight mt-0.5">
            Pick A Plan To Keep The Tools Open.
          </div>
        </div>
        <span className="inline-flex items-center gap-1 px-4 py-2.5 rounded-md text-sm font-bold uppercase tracking-[0.16em]"
              style={{ background: "#EAF6FC", color: "#0F172A" }}>
          See Plans <ChevronRight className="w-4 h-4"/>
        </span>
      </div>
    </Link>
  ) : (
    <Link to="/pricing"
          data-testid="trial-banner-countdown"
          className="block rounded-2xl p-4 shadow-sm hover:shadow-md transition no-underline"
          style={{ background: "#E0F2FE", border: "2px solid #F39A1F" }}>
      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex-1 min-w-[200px] text-sm" style={{ color: "#0F172A" }}>
          <strong>{days === 0 ? "Trial ends today." : days === 1 ? "1 day left" : `${days} days left`}</strong>
          {" "}in your 7-day free trial. Lock in Pro at $49/mo before the tools turn off.
        </div>
        <span className="text-[11px] uppercase tracking-[0.18em] font-bold inline-flex items-center gap-1" style={{ color: "#0F172A" }}>
          Upgrade <ChevronRight className="w-4 h-4"/>
        </span>
      </div>
    </Link>
  );
}

/* ---- Prayer Box widget ---- */
function PrayerBox() {
  const { user } = useAuth();
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (body.trim().length < 4) { toast.error("Share a few words about your request."); return; }
    setBusy(true);
    try {
      await api.post("/prayer-requests", {
        body: body.trim(),
        private: true,
        name: user?.full_name || "",
        email: user?.email || "",
      });
      toast.success("Your prayer reached Mike. He prays over every one nightly.");
      setBody("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not send. Please try again.");
    }
    setBusy(false);
  };
  return (
    <form onSubmit={submit} data-testid="cmd-prayer-box" className="rounded-xl bg-white border-2 p-5" style={{ borderColor: "#F39A1F" }}>
      <div className="flex items-center gap-2 mb-2">
        <Heart className="w-5 h-5 text-red-wine fill-current"/>
        <div className="text-[10px] tracking-[0.28em] uppercase font-bold text-red-wine">★ Prayer Box · Private to Mike</div>
      </div>
      <h3 className="font-serif-display text-xl md:text-2xl text-red-wine leading-tight">Send Mike a prayer request.</h3>
      <p className="text-sm text-foreground/80 mt-1">He reads and prays over every one before bed.</p>
      <textarea
        rows={3}
        value={body}
        onChange={(e) => setBody(e.target.value)}
        placeholder="Write your prayer here…"
        data-testid="cmd-prayer-body"
        className="w-full mt-3 px-3 py-2.5 rounded-md border-2 border-border focus:border-arama outline-none transition text-base resize-y"
      />
      <button type="submit" disabled={busy} data-testid="cmd-prayer-submit"
              className="btn-primary w-full mt-3 inline-flex items-center justify-center gap-2 disabled:opacity-60">
        <Send className="w-4 h-4"/> {busy ? "Sending…" : "Send To Mike"}
      </button>
    </form>
  );
}

/* ============================================================
   MAIN COMPONENT
   ============================================================ */
export default function Overview() {
  const { user } = useAuth();
  const [daily, setDaily] = useState(null);
  const tier = (user?.tier || "free").toLowerCase();
  const _email = (user?.email || "").toLowerCase().trim();
  const _isFounder = user?.is_admin === true || _email === "mylivegoodplan@gmail.com";
  const firstName = _isFounder ? "Michael" : ((user?.full_name || "friend").split(" ")[0] || "friend");

  useEffect(() => {
    api.get("/dashboard/daily-message").then(r => setDaily(r.data)).catch(() => {});
  }, []);

  const tierBadge = tier === "free" ? "Free Member" : (tier === "faithful" ? "Pro" : (tier === "pro" || tier === "ministry_pro" ? "Unlimited" : tier));

  return (
    <div className="space-y-5 p-3 sm:p-5 rounded-2xl" data-testid="cmd-center">
      <TrialBanner user={user} />

      {/* ─────────── COMPACT TOP STRIP — small greeting + clock + tier ─────────── */}
      <div className="rounded-xl bg-white border shadow-sm px-4 py-3 flex items-center gap-4 flex-wrap" style={{ borderColor: "#E5E7EB" }}>
        <div className="flex-1 min-w-[160px]">
          <div className="text-[9px] tracking-[0.28em] uppercase font-bold opacity-70" style={{ color: "#2E9F3A" }}>★ <Brand suffix=".COM"/> · Command Center</div>
          <div className="font-serif-display text-base sm:text-lg text-red-wine leading-tight" style={{ fontWeight: 700 }}>
            {greetingForHour()}, {firstName}.
          </div>
        </div>
        <div className="text-xs text-foreground/70 hidden sm:block">
          <LiveClock compact/>
        </div>
        <div className="flex items-center gap-1.5 px-2 py-1 rounded-full bg-[#FFFBEF]" style={{ border: "1px solid #F39A1F55" }}>
          <Crown className="w-3.5 h-3.5" style={{ color: "#F39A1F" }}/>
          <span className="text-[10px] tracking-[0.18em] font-bold uppercase text-red-wine">{tierBadge}</span>
        </div>
      </div>

      {/* ─────────── CLICKFUNNELS-STYLE TOOL GRID — the centerpiece ─────────── */}
      <DashboardGrid />

      {/* ─────────── DAILY VERSE + WORD FROM FAITHARAMA — moved below grid, much smaller ─────────── */}
      {daily && (
        <div className="grid sm:grid-cols-2 gap-3">
          <blockquote data-testid="cmd-verse" className="rounded-xl bg-white border shadow-sm px-4 py-3" style={{ borderColor: "#E5E7EB" }}>
            <div className="text-[9px] tracking-[0.28em] uppercase font-bold mb-0.5" style={{ color: "#F39A1F" }}>★ Verse of the Day · NKJV</div>
            <p className="font-serif-display text-sm md:text-base text-foreground italic leading-snug" style={{ textWrap: "balance" }}>
              &ldquo;{daily.verse?.text}&rdquo;
            </p>
            <footer className="mt-1 text-[11px] text-red-wine font-bold tracking-wider-cap">— {daily.verse?.reference}</footer>
          </blockquote>
          <div data-testid="cmd-mike-note" className="rounded-xl bg-white border shadow-sm px-4 py-3" style={{ borderColor: "#E5E7EB" }}>
            <div className="text-[9px] tracking-[0.28em] uppercase font-bold mb-0.5" style={{ color: "#F39A1F" }}>★ A Word From <Brand suffix=".COM"/></div>
            {daily.mike_note ? (
              <p className="font-serif-display text-sm md:text-base text-foreground italic leading-snug" style={{ textWrap: "balance" }}>
                &ldquo;{daily.mike_note}&rdquo;
              </p>
            ) : (
              <p className="text-sm text-foreground/80 italic leading-snug">
                Take five quiet minutes today. Stretch. Drink a glass of water. Open the Word. Small steps, every day &mdash; that&apos;s how a healthy life is built.
              </p>
            )}
            <footer className="mt-1 text-[11px] text-red-wine font-bold tracking-wider-cap">— <Brand suffix=".com"/></footer>
          </div>
        </div>
      )}

      {/* ─────────── SET YOUR OWN PASSWORD — moved below verse, hides itself once set ─────────── */}
      <SetPasswordCard />

      {/* ─────────── PRAYER BOX — side car under the grid ─────────── */}
      <div className="grid lg:grid-cols-[1fr_360px] gap-5 items-start">
        <div />
        <PrayerBox/>
      </div>
    </div>
  );
}

/* ════════════════════════════════════════════════════════════════════
   SET YOUR OWN PASSWORD CARD
   ════════════════════════════════════════════════════════════════════ */
function SetPasswordCard() {
  const [cur, setCur] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [show, setShow] = useState(true);
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);

  const save = async (e) => {
    e?.preventDefault?.();
    if (saving) return;
    const a = cur.trim();
    const b = next.trim();
    const c = confirm.trim();
    if (!a) return toast.error("Please type your current password first.");
    if (b.length < 6) return toast.error("New password needs at least 6 characters.");
    if (b !== c) return toast.error("The two new passwords don't match. Try again.");
    setSaving(true);
    try {
      await api.post("/auth/change-password", { current_password: a, new_password: b });
      setDone(true);
      setCur(""); setNext(""); setConfirm("");
      toast.success("★ Password saved. That's your new login password now.");
    } catch (err) {
      const detail = err?.response?.data?.detail || "Couldn't save the password. Is your current one correct?";
      toast.error(detail);
    } finally { setSaving(false); }
  };

  return (
    <div data-testid="cmd-set-password"
         className="rounded-2xl border-4 shadow-md p-5 sm:p-6"
         style={{ background: "linear-gradient(135deg, #FFFFFF 0%, #EAF6FC 100%)", borderColor: "#2E9F3A" }}>
      <div className="flex items-start gap-3 mb-4">
        <div className="w-11 h-11 rounded-xl flex items-center justify-center shrink-0 text-white"
             style={{ background: "#2E9F3A" }}>
          <KeyRound className="w-5 h-5" />
        </div>
        <div className="flex-1">
          <div className="text-[10px] tracking-[0.32em] uppercase font-bold" style={{ color: "#2E9F3A" }}>
            ★ FIRST THING TO DO
          </div>
          <h3 className="font-serif-display text-2xl md:text-3xl uppercase leading-tight" style={{ color: "#0F172A" }}>
            Set Your Own Password.
          </h3>
          <p className="text-sm text-foreground/75 mt-1">
            Pick something you&apos;ll remember. Saves to your account immediately — no logout needed.
          </p>
        </div>
      </div>

      {done ? (
        <div className="rounded-xl bg-white p-4 border-2" style={{ borderColor: "#2E9F3A" }}>
          <div className="font-bold uppercase tracking-[0.22em] text-sm" style={{ color: "#2E9F3A" }}>
            ✓ PASSWORD SAVED
          </div>
          <p className="text-sm mt-2">
            That&apos;s your new login password starting now. Log out and back in any time — it&apos;ll work.
          </p>
          <button type="button" onClick={() => setDone(false)}
                  className="mt-3 text-xs uppercase tracking-[0.18em] font-bold hover:opacity-70"
                  style={{ color: "#0F172A" }}
                  data-testid="cmd-set-password-again">
            Change It Again →
          </button>
        </div>
      ) : (
        <form onSubmit={save} className="space-y-3">
          <PwField label="Current password (or the temp one we just sent you)" value={cur} onChange={setCur} show={show} testid="cmd-pw-current"/>
          <PwField label="Your NEW password" value={next} onChange={setNext} show={show} testid="cmd-pw-new"/>
          <PwField label="Type your NEW password one more time" value={confirm} onChange={setConfirm} show={show} testid="cmd-pw-confirm"/>

          <label className="flex items-center gap-2 text-sm cursor-pointer select-none" data-testid="cmd-pw-showtoggle">
            <input type="checkbox" checked={show} onChange={(e) => setShow(e.target.checked)} className="w-4 h-4" />
            <span style={{ color: "#0F172A" }}>Show my passwords (so I can see what I typed)</span>
          </label>

          <button type="submit" disabled={saving}
                  data-testid="cmd-pw-save"
                  className="w-full inline-flex items-center justify-center gap-2 px-7 py-4 rounded-md text-base font-bold uppercase tracking-[0.18em] text-white shadow-lg hover:scale-[1.01] transition disabled:opacity-60"
                  style={{ background: "#2E9F3A" }}>
            {saving ? "Saving…" : "★ Save My New Password"}
          </button>
        </form>
      )}
    </div>
  );
}

function PwField({ label, value, onChange, show, testid }) {
  return (
    <div>
      <label className="block text-[11px] tracking-[0.18em] uppercase font-bold mb-1.5" style={{ color: "#0F172A" }}>
        {label}
      </label>
      <input
        type={show ? "text" : "password"}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        autoComplete="off"
        data-testid={testid}
        className="w-full px-4 py-3 rounded-md border-2 bg-white text-lg font-mono outline-none focus:ring-2 focus:ring-[#F39A1F]"
        style={{ borderColor: "#F39A1F55" }}
      />
    </div>
  );
}
